import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * Display preferences that are neither the theme nor the language.
 *
 * Warning: Client-side only: the backend has no column for them, and they are device
 * settings rather than account ones — the same choice is not necessarily wanted
 * on a phone and on a desktop.
 */
const REDUCE_MOTION_KEY = 'meeply.reduceMotion';

interface PreferencesContextValue {
  /** Coupe transitions et animations dans toute l'application. */
  reduceMotion: boolean;
  setReduceMotion: (value: boolean) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

function readStored(): boolean {
  try {
    return localStorage.getItem(REDUCE_MOTION_KEY) === 'true';
  } catch {
    return false;
  }
}

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [reduceMotion, setReduceMotionState] = useState(readStored);

  // The DOM is an external system, which is the only legitimate use of an
  // effect here. `theme.css` reacts to `[data-reduce-motion='true']`.
  useEffect(() => {
    document.documentElement.dataset.reduceMotion = String(reduceMotion);
  }, [reduceMotion]);

  const setReduceMotion = useCallback((value: boolean) => {
    setReduceMotionState(value);
    try {
      localStorage.setItem(REDUCE_MOTION_KEY, String(value));
    } catch {
      /* preference not persisted: harmless */
    }
  }, []);

  const value = useMemo(
    () => ({ reduceMotion, setReduceMotion }),
    [reduceMotion, setReduceMotion],
  );

  return <PreferencesContext value={value}>{children}</PreferencesContext>;
}

export function usePreferences(): PreferencesContextValue {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error(
      'usePreferences doit être utilisé dans un <PreferencesProvider>.',
    );
  }
  return context;
}
